import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { CRMLayout } from "@/components/CRMLayout";
import { toast } from "sonner";
import {
  Sparkles, Send, Loader2, Check, X, RefreshCw, Plus, Image as ImageIcon,
  Wand2, ChevronRight, Play, Pause, LayoutGrid, List as ListIcon, Search, Layers,
  ArrowUpDown, ArrowUp, ArrowDown, CalendarClock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import PubTrackingAgent from "@/components/PubTrackingAgent";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const ETATS = [
  { cle: "diffuse", titre: "En diffusion", point: "bg-emerald-500", puce: "bg-emerald-100 text-emerald-700" },
  { cle: "demarrage", titre: "En démarrage", point: "bg-violet-500", puce: "bg-violet-100 text-violet-700" },
  { cle: "planifiee", titre: "Planifiée", point: "bg-sky-500", puce: "bg-sky-100 text-sky-700" },
  { cle: "pause", titre: "En pause", point: "bg-slate-400", puce: "bg-slate-100 text-slate-600" },
  { cle: "casse", titre: "À réparer", point: "bg-rose-500", puce: "bg-rose-100 text-rose-700" },
  { cle: "terminee", titre: "Terminées", point: "bg-amber-500", puce: "bg-amber-100 text-amber-700" },
];
const ETAT = Object.fromEntries(ETATS.map((e) => [e.cle, e]));
const RATIOS = { square: "aspect-square", portrait: "aspect-[4/5]", landscape: "aspect-video" };

const OBJECTIFS = [
  { id: "OUTCOME_LEADS", libelle: "Prospects (formulaire)" },
  { id: "OUTCOME_TRAFFIC", libelle: "Trafic vers le site" },
  { id: "OUTCOME_ENGAGEMENT", libelle: "Interactions" },
  { id: "OUTCOME_AWARENESS", libelle: "Notoriété" },
  { id: "OUTCOME_SALES", libelle: "Ventes" },
];

// FastAPI renvoie un tableau d'objets sur une erreur de validation : passe tel
// quel a un bandeau, React refuse de l'afficher et la page devient blanche.
const messageErreur = (e, defaut) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" · ");
  if (d) return JSON.stringify(d);
  return defaut;
};

const euros = (n) =>
  (n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
const entier = (n) => (n || 0).toLocaleString("fr-FR");
const dateFr = (valeur) => {
  if (!valeur) return "—";
  const [annee, mois, jour] = valeur.slice(0, 10).split("-");
  return jour && mois && annee ? `${jour}/${mois}/${annee}` : valeur;
};

function Etat({ valeur, fantome }) {
  const e = ETAT[valeur] || ETAT.pause;
  return (
    <span className={`text-[11px] px-2 py-1 rounded-md whitespace-nowrap ${
      fantome ? "bg-amber-100 text-amber-800" : e.puce}`}>
      {["diffuse", "demarrage"].includes(valeur) ? <Play className="w-3 h-3 inline mr-1" />
        : valeur === "planifiee" ? <CalendarClock className="w-3 h-3 inline mr-1" />
        : <Pause className="w-3 h-3 inline mr-1" />}
      {fantome ? "Fantôme" : e.titre}
    </span>
  );
}

export default function PubStudioPage() {
  const { token } = useAuth();
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const [vue, setVue] = useState("liste");
  const [comptes, setComptes] = useState([]);
  const [filtreCompte, setFiltreCompte] = useState("tous");
  const [filtreActivite, setFiltreActivite] = useState("toutes");
  const [recherche, setRecherche] = useState("");
  const [tri, setTri] = useState({ cle: "depense", direction: "desc" });
  const [campagnes, setCampagnes] = useState(null);
  const [resume, setResume] = useState({});
  const [veille, setVeille] = useState(null);
  const [ouvertes, setOuvertes] = useState({});
  const [details, setDetails] = useState({});
  const [fiche, setFiche] = useState(null);
  const [occupe, setOccupe] = useState(false);

  const [studioOuvert, setStudioOuvert] = useState(false);
  const [briefs, setBriefs] = useState([]);
  const [brief, setBrief] = useState(null);
  const [visuels, setVisuels] = useState([]);
  const [messages, setMessages] = useState([]);
  const [saisie, setSaisie] = useState("");
  const [enCours, setEnCours] = useState(false);
  const [flux, setFlux] = useState("");
  const [dessine, setDessine] = useState(null);
  const [autoVisuels, setAutoVisuels] = useState(true);
  const [modeles, setModeles] = useState([]);
  const [modele, setModele] = useState("");
  const [agrandi, setAgrandi] = useState(null);
  const [formulaire, setFormulaire] = useState(null);
  const [creation, setCreation] = useState(false);
  const [agentSuiviOuvert, setAgentSuiviOuvert] = useState(false);
  const filRef = useRef(null);

  // ------------------------------------------------------------- chargement
  const charger = useCallback(async () => {
    setCampagnes(null); setOuvertes({}); setDetails({});
    try {
      const url = filtreCompte === "tous"
        ? `${API_URL}/api/pub/campagnes`
        : `${API_URL}/api/pub/campagnes?compte=${filtreCompte}`;
      const { data } = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setCampagnes(data.campagnes || []);
      setResume({ total: data.total_depense, parEtat: data.par_etat || {} });
      (data.soucis || []).forEach((s) => toast.error(`${s.compte} : ${s.erreur}`));
    } catch (e) {
      toast.error(messageErreur(e, "Lecture Meta impossible"));
      setCampagnes([]);
    }
  }, [filtreCompte, token]);

  useEffect(() => { charger(); }, [charger]);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: c }, { data: m }] = await Promise.all([
          axios.get(`${API_URL}/api/pub/comptes-stats`, auth),
          axios.get(`${API_URL}/api/pub/modeles`, auth).catch(() => ({ data: {} })),
        ]);
        setComptes(c.comptes || []);
        setModeles(m.modeles || []);
        setModele(m.defaut || "");
      } catch { /* la barre reste vide */ }
      try {
        const { data: v } = await axios.get(`${API_URL}/api/pub/veille`, auth);
        setVeille(v);
      } catch { setVeille({ alertes: [] }); }
    })();
  }, []);

  useEffect(() => {
    if (filRef.current) filRef.current.scrollTop = filRef.current.scrollHeight;
  }, [messages, flux, dessine, visuels]);

  // ------------------------------------------------------------- campagnes
  const basculer = async (camp) => {
    const ouvert = !ouvertes[camp.id];
    setOuvertes((o) => ({ ...o, [camp.id]: ouvert }));
    if (!ouvert || details[camp.id]) return;
    try {
      const { data } = await axios.get(
        `${API_URL}/api/pub/campagnes/${camp.id}/ensembles?compte=${camp.compte_id}`, auth);
      const ensembles = data.ensembles || [];
      const avecPubs = await Promise.all(ensembles.map(async (e) => {
        try {
          const { data: p } = await axios.get(
            `${API_URL}/api/pub/ensembles/${e.id}/publicites?compte=${camp.compte_id}`, auth);
          return { ...e, publicites: p.publicites || [] };
        } catch { return { ...e, publicites: [] }; }
      }));
      setDetails((d) => ({ ...d, [camp.id]: avecPubs }));
    } catch (e) {
      toast.error(messageErreur(e, "Meta n'a pas répondu"));
      setDetails((d) => ({ ...d, [camp.id]: [] }));
    }
  };

  const changerStatut = async (camp, statut) => {
    const mot = statut === "PAUSED" ? "mettre en pause" : "relancer la diffusion de";
    if (!window.confirm(`Voulez-vous ${mot} « ${camp.nom} » chez Meta ?`)) return;
    setOccupe(true);
    try {
      await axios.post(
        `${API_URL}/api/pub/objets/${camp.id}/statut?compte=${camp.compte_id}&statut=${statut}`,
        {}, auth);
      toast.success(statut === "PAUSED" ? "Mise en pause chez Meta" : "Diffusion relancée");
      setFiche(null);
      charger();
    } catch (e) {
      toast.error(messageErreur(e, "Meta a refusé"));
    } finally { setOccupe(false); }
  };

  const changerTri = (cle) => {
    setTri((actuel) => ({
      cle,
      direction: actuel.cle === cle && actuel.direction === "asc" ? "desc" : "asc",
    }));
  };

  const valeurTri = (campagne, cle) => {
    if (cle === "campagne") return campagne.nom || "";
    if (cle === "compte") return campagne.compte_nom || "";
    if (cle === "etat") return (ETAT[campagne.etat]?.titre || campagne.etat || "");
    if (cle === "creation") return campagne.creation || "";
    return Number(campagne[cle]) || 0;
  };

  const visibles = (campagnes || [])
    .filter((c) => filtreActivite === "toutes"
      || (filtreActivite === "actives"
        ? ["diffuse", "demarrage", "planifiee", "casse"].includes(c.etat)
        : ["pause", "terminee"].includes(c.etat)))
    .filter((c) => !recherche.trim() || (c.nom || "").toLowerCase().includes(recherche.toLowerCase()))
    .sort((a, b) => {
      const av = valeurTri(a, tri.cle);
      const bv = valeurTri(b, tri.cle);
      const resultat = typeof av === "string"
        ? av.localeCompare(bv, "fr", { sensitivity: "base", numeric: true })
        : av - bv;
      return tri.direction === "asc" ? resultat : -resultat;
    });

  const EnteteTri = ({ cle, children, aligne = "left" }) => {
    const active = tri.cle === cle;
    const Icone = !active ? ArrowUpDown : tri.direction === "asc" ? ArrowUp : ArrowDown;
    return (
      <button type="button" onClick={() => changerTri(cle)}
        className={`flex items-center gap-1 hover:text-slate-700 transition-colors select-none ${
          aligne === "right" ? "justify-end text-right" : "text-left"
        } ${active ? "text-indigo-600 font-semibold" : ""}`}
        title={`Trier par ${children}`} aria-label={`Trier par ${children}`}>
        <span>{children}</span><Icone className="w-3 h-3 shrink-0" />
      </button>
    );
  };

  // ------------------------------------------------------------- studio
  const ouvrirStudio = async () => {
    setStudioOuvert(true);
    if (briefs.length) return;
    try {
      const { data } = await axios.get(`${API_URL}/api/pub/briefs`, auth);
      setBriefs(data);
      if (data.length) ouvrirBrief(data[0].id);
    } catch { /* aucun brief */ }
  };

  const ouvrirBrief = async (id) => {
    const { data } = await axios.get(`${API_URL}/api/pub/briefs/${id}`, auth);
    setBrief(data); setVisuels(data.visuels || []); setMessages(data.messages || []); setFlux("");
  };

  // Un formulaire plutot qu'une invite : le cadrage saisi ici part d'un bloc a
  // ChatGPT, qui ne repose donc pas les questions dont on a deja la reponse.
  const ouvrirFormulaire = (depuis) => {
    if (!comptes.length) return toast.error("Aucun compte publicitaire");
    setFormulaire({
      titre: "",
      config_id: depuis?.compte_id || comptes[0].id,
      objectif: depuis?.objectif_brut || "OUTCOME_LEADS",
      zone: "",
      audience: "",
      budget_jour: depuis?.budget_jour || 20,
      duree_jours: 14,
      consigne: depuis ? `Reprends l'esprit de « ${depuis.nom} » pour une nouvelle campagne.` : "",
    });
  };

  const lancerCreation = async () => {
    const f = formulaire;
    if (!f.consigne.trim()) return toast.error("Décrivez ce que vous voulez vendre");
    setCreation(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/pub/briefs`, {
        titre: f.titre.trim() || f.consigne.trim().slice(0, 60),
        config_id: f.config_id,
        objectif: f.objectif,
        zone: f.zone,
        audience: f.audience,
        budget_jour: Number(f.budget_jour) || 0,
        duree_jours: Number(f.duree_jours) || 0,
        consigne: f.consigne,
      }, auth);
      const { data: liste } = await axios.get(`${API_URL}/api/pub/briefs`, auth);
      setBriefs(liste);
      setBrief(data); setVisuels([]); setMessages([]); setFlux("");
      setFormulaire(null); setStudioOuvert(true);
      envoyer(data.message_initial, data);
    } catch (e) {
      toast.error(messageErreur(e, "Création impossible"));
    } finally { setCreation(false); }
  };

  const envoyer = async (texteForce, briefForce) => {
    const cible = briefForce || brief;
    const texte = (typeof texteForce === "string" ? texteForce : saisie).trim();
    if (!texte || !cible || enCours) return;
    if (typeof texteForce !== "string") setSaisie("");
    setMessages((m) => [...m, { role: "user", texte }]);
    setEnCours(true); setFlux("");
    let accumule = "";
    try {
      const res = await fetch(`${API_URL}/api/pub/briefs/${cible.id}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: texte, auto_visuels: autoVisuels, modele }),
      });
      if (!res.ok || !res.body) throw new Error("réponse invalide");
      const lecteur = res.body.getReader();
      const decodeur = new TextDecoder();
      let reste = "";
      while (true) {
        const { done, value } = await lecteur.read();
        if (done) break;
        reste += decodeur.decode(value, { stream: true });
        const lignes = reste.split("\n");
        reste = lignes.pop();
        for (const ligne of lignes) {
          if (!ligne.startsWith("data: ")) continue;
          let evt;
          try { evt = JSON.parse(ligne.slice(6)); } catch { continue; }
          if (evt.type === "texte") { accumule += evt.contenu; setFlux(accumule); }
          else if (evt.type === "visuel_debut") setDessine(evt);
          else if (evt.type === "visuel_echec") toast.error(`${evt.titre} : ${evt.message}`);
          else if (evt.type === "fin") {
            setMessages((m) => [...m, { role: "assistant", texte: evt.reponse }]);
            setFlux(""); setDessine(null); setVisuels(evt.visuels || []);
          } else if (evt.type === "error") toast.error(evt.message);
        }
      }
    } catch { toast.error("ChatGPT n'a pas répondu"); }
    finally { setEnCours(false); setFlux(""); setDessine(null); }
  };

  const suffixe = () => (modele ? `?modele=${encodeURIComponent(modele)}` : "");

  const regenerer = async (v) => {
    setVisuels((l) => l.map((x) => (x.id === v.id ? { ...x, occupe: true } : x)));
    try {
      const { data } = await axios.post(
        `${API_URL}/api/pub/visuels/${v.id}/regenerer${suffixe()}`, {}, auth);
      setVisuels((l) => l.map((x) => (x.id === data.id ? { ...data, cache: Date.now() } : x)));
    } catch (e) {
      toast.error(messageErreur(e, "Régénération impossible"));
      setVisuels((l) => l.map((x) => (x.id === v.id ? { ...x, occupe: false } : x)));
    }
  };

  const juger = async (v, etat) => {
    setVisuels((l) => l.map((x) => (x.id === v.id ? { ...x, etat } : x)));
    try { await axios.patch(`${API_URL}/api/pub/visuels/${v.id}`, { etat }, auth); }
    catch { toast.error("Changement refusé"); }
  };

  const compteur = (cle) => (resume.parEtat || {})[cle] || 0;

  // ------------------------------------------------------------- rendu
  return (
    <CRMLayout>
      <div className="flex h-[calc(100dvh-4rem)] min-w-0 flex-col bg-slate-50 lg:h-screen">

        <div className="flex flex-wrap items-center gap-4 border-b border-slate-200 bg-white px-4 py-3 sm:gap-5 sm:px-6">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div>
              <h1 className="text-base font-semibold text-slate-900 leading-tight">Régie Meta</h1>
              <p className="text-[11px] text-slate-400">lu en direct chez Meta</p>
            </div>
          </div>

          <div className="ml-auto flex items-center gap-4 sm:gap-6">
            <div>
              <div className="text-[17px] font-semibold text-amber-700 tabular-nums">
                {euros(resume.total)}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400">dépensé · 30 j</div>
            </div>
            <div>
              <div className={`text-[17px] font-semibold tabular-nums ${
                compteur("diffuse") ? "text-emerald-600" : "text-rose-600"}`}>
                {compteur("diffuse")}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400">en diffusion</div>
            </div>
            <div>
              <div className="text-[17px] font-semibold text-slate-900 tabular-nums">
                {(campagnes || []).length}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400">campagnes</div>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 bg-white px-4 py-2.5 sm:px-6">
          <div className="flex bg-slate-100 rounded-lg p-0.5">
            {[["liste", ListIcon, "Liste"], ["kanban", LayoutGrid, "Kanban"]].map(([cle, Icone, titre]) => (
              <button key={cle} onClick={() => setVue(cle)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition ${
                  vue === cle ? "bg-white shadow-sm text-slate-900 font-medium" : "text-slate-500"}`}>
                <Icone className="w-4 h-4" /> {titre}
              </button>
            ))}
          </div>

          <div className="w-px h-5 bg-slate-200" />

          <div className="flex bg-slate-100 rounded-lg p-0.5" aria-label="Filtrer les campagnes par activité">
            {[
              ["toutes", "Toutes", (campagnes || []).length],
              ["actives", "Actives", (campagnes || []).filter((c) => ["diffuse", "demarrage", "planifiee", "casse"].includes(c.etat)).length],
              ["inactives", "Non actives", (campagnes || []).filter((c) => ["pause", "terminee"].includes(c.etat)).length],
            ].map(([cle, titre, nombre]) => (
              <button key={cle} type="button" onClick={() => setFiltreActivite(cle)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md transition ${
                  filtreActivite === cle
                    ? "bg-white shadow-sm text-slate-900 font-medium"
                    : "text-slate-500 hover:text-slate-700"
                }`}>
                {titre}
                <span className={`text-[10px] tabular-nums ${filtreActivite === cle ? "text-indigo-600" : "text-slate-400"}`}>
                  {nombre}
                </span>
              </button>
            ))}
          </div>

          <div className="w-px h-5 bg-slate-200" />

          <div className="flex gap-1.5 flex-wrap">
            <button onClick={() => setFiltreCompte("tous")}
              className={`text-xs px-3 py-1.5 rounded-full border transition ${
                filtreCompte === "tous" ? "bg-slate-900 text-white border-slate-900"
                                        : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
              Tous les comptes
            </button>
            {comptes.map((c) => (
              <button key={c.id} onClick={() => setFiltreCompte(c.id)}
                className={`text-xs px-3 py-1.5 rounded-full border transition flex items-center gap-2 ${
                  filtreCompte === c.id ? "bg-slate-900 text-white border-slate-900"
                                        : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
                {c.nom}
                <span className="opacity-60 tabular-nums">{euros(c.depense)}</span>
              </button>
            ))}
          </div>

          <div className="relative w-full sm:ml-auto sm:w-auto">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={recherche} onChange={(e) => setRecherche(e.target.value)}
              placeholder="Rechercher une campagne…"
              className="w-full rounded-lg border border-slate-200 bg-slate-50 py-1.5 pl-8 pr-3 text-sm sm:w-56" />
          </div>

          <Button size="sm" variant="outline" onClick={ouvrirStudio}>
            <Sparkles className="w-4 h-4 mr-1.5" /> Studio
          </Button>
          <Button size="sm" variant="outline" onClick={() => setAgentSuiviOuvert(true)}
                  className="border-violet-200 text-violet-700 hover:bg-violet-50">
            <Sparkles className="w-4 h-4 mr-1.5" /> Agent suivi
          </Button>
          <Button size="sm" onClick={() => ouvrirFormulaire(null)}
                  className="bg-gradient-to-r from-violet-600 to-indigo-600">
            <Plus className="w-4 h-4 mr-1.5" /> Créer une campagne
          </Button>
        </div>

        <div className="flex-1 min-h-0 flex">
          <div className="flex-1 min-w-0 overflow-auto">
            {campagnes === null ? (
              <div className="flex items-center justify-center py-20 text-slate-400">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Lecture chez Meta…
              </div>
            ) : vue === "liste" ? (
              <div className="bg-white">
                <div className="grid grid-cols-[18px_minmax(260px,1fr)_130px_100px_110px_90px_110px] gap-3
                                px-6 py-2 border-b border-slate-200 text-[10px] uppercase
                                tracking-wider text-slate-400 sticky top-0 bg-white z-10">
                  <span />
                  <EnteteTri cle="campagne">Campagne</EnteteTri>
                  <EnteteTri cle="compte">Compte</EnteteTri>
                  <EnteteTri cle="creation">Création</EnteteTri>
                  <EnteteTri cle="depense" aligne="right">Dépensé</EnteteTri>
                  <EnteteTri cle="clics" aligne="right">Clics</EnteteTri>
                  <EnteteTri cle="etat" aligne="right">État</EnteteTri>
                </div>

                {visibles.map((c) => (
                  <div key={c.id} className="border-b border-slate-100">
                    <div onClick={() => basculer(c)}
                      className={`grid grid-cols-[18px_minmax(260px,1fr)_130px_100px_110px_90px_110px] gap-3
                                  items-center px-6 py-2.5 hover:bg-slate-50 cursor-pointer ${
                                  ouvertes[c.id] ? "bg-slate-50" : ""}`}>
                      <ChevronRight className={`w-3.5 h-3.5 text-slate-400 transition-transform ${
                        ouvertes[c.id] ? "rotate-90" : ""}`} />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 min-w-0 flex-wrap">
                          <div className="text-sm text-slate-800 truncate min-w-[170px] flex-1">{c.nom}</div>
                          {!!c.impressions && (
                            <span className="shrink-0 text-[10px] px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 tabular-nums">
                              {entier(c.impressions)} impr. · CTR {(c.ctr || 0).toLocaleString("fr-FR", { maximumFractionDigits: 2 })} % · CPC {euros(c.cpc)}
                            </span>
                          )}
                          {c.resultat_libelle && (
                            <span className={`shrink-0 text-[10px] px-2 py-0.5 rounded-full tabular-nums ${
                              c.resultats ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"
                            }`}>
                              Résultat : {entier(c.resultats)} {c.resultat_libelle}{c.resultats > 1 ? "s" : ""}
                              {c.cout_resultat ? ` · ${euros(c.cout_resultat)}/${c.resultat_libelle}` : ""}
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-slate-400">
                          Objectif : {c.objectif || "non précisé"}{c.debut ? ` · depuis le ${c.debut}` : ""}
                          {c.fin ? ` → ${c.fin}` : ""}
                        </div>
                        {c.recommandation && (
                          <div className="flex items-center gap-1 mt-0.5 text-[10px] text-violet-700 truncate" title={c.recommandation}>
                            <Sparkles className="w-3 h-3 shrink-0" />
                            <span className="font-semibold shrink-0">Reco IA :</span>
                            <span className="truncate">{c.recommandation}</span>
                          </div>
                        )}
                      </div>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100
                                       text-slate-600 border border-slate-200 truncate">
                        {c.compte_nom}
                      </span>
                      <span className={`text-xs tabular-nums ${
                        c.creation ? "text-slate-600" : "text-slate-300"}`}>
                        {dateFr(c.creation)}
                      </span>
                      <span className={`text-sm text-right tabular-nums ${
                        c.depense > 0 ? "text-slate-900 font-medium" : "text-slate-300"}`}>
                        {c.depense > 0 ? euros(c.depense) : "—"}
                      </span>
                      <span className={`text-sm text-right tabular-nums ${
                        c.clics ? "text-slate-600" : "text-slate-300"}`}>
                        {c.clics ? entier(c.clics) : "—"}
                      </span>
                      <button className="text-right" onClick={(e) => { e.stopPropagation(); setFiche(c); }}>
                        <Etat valeur={c.etat} fantome={c.fantome} />
                      </button>
                    </div>

                    {ouvertes[c.id] && (
                      <div className="pl-12 pr-6 pb-4 bg-white">
                        {!details[c.id] ? (
                          <div className="flex items-center gap-2 text-xs text-slate-400 py-3">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Lecture des ensembles…
                          </div>
                        ) : !details[c.id].length ? (
                          <p className="text-xs text-slate-400 py-3">
                            Aucun ensemble de publicités chez Meta.
                          </p>
                        ) : details[c.id].map((e) => (
                          <div key={e.id} className="border-l-2 border-slate-200 pl-4 py-2 mb-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Layers className="w-3.5 h-3.5 text-indigo-500" />
                              <span className="text-sm font-medium text-slate-800">{e.nom}</span>
                              <Etat valeur={e.statut === "ACTIVE" ? "diffuse" : "pause"} />
                            </div>
                            <p className="text-[11px] text-slate-400 mt-0.5">
                              {e.zone || "zone non précisée"} · {e.ages}
                              {e.budget_jour ? ` · ${e.budget_jour} €/j` : " · budget campagne"}
                              {e.fin ? ` · fin le ${e.fin}` : ""}
                              {` · ${(e.publicites || []).length} annonce${(e.publicites || []).length > 1 ? "s" : ""}`}
                            </p>
                            {!!(e.publicites || []).length && (
                              <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                                <div className="flex items-center gap-2 mb-3">
                                  <ImageIcon className="w-4 h-4 text-indigo-600" />
                                  <span className="text-sm font-semibold text-slate-800">
                                    {(e.publicites || []).length} visuels publicitaires
                                  </span>
                                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                                    e.publicites.some((p) => p.statut === "ACTIVE")
                                      ? "bg-emerald-100 text-emerald-700"
                                      : "bg-slate-200 text-slate-600"
                                  }`}>
                                    {e.publicites.filter((p) => p.statut === "ACTIVE").length
                                      ? `${e.publicites.filter((p) => p.statut === "ACTIVE").length} active${e.publicites.filter((p) => p.statut === "ACTIVE").length > 1 ? "s" : ""}`
                                      : "En pause"}
                                  </span>
                                  <span className="ml-auto text-[11px] text-slate-400">
                                    Cliquez sur une image pour l’agrandir
                                  </span>
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3">
                                  {e.publicites.map((p) => (
                                    <div key={p.id}
                                         className="min-w-0 rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
                                      {p.image ? (
                                        <button onClick={() => setAgrandi({ ...p, url: p.image })}
                                                className="block w-full aspect-square bg-slate-100 hover:opacity-95 transition">
                                          <img src={p.image} alt={p.nom}
                                               loading="lazy"
                                               className="w-full h-full object-cover" />
                                        </button>
                                      ) : (
                                        <div className="aspect-square bg-slate-50 flex items-center
                                                        justify-center text-slate-300">
                                          <ImageIcon className="w-8 h-8" />
                                        </div>
                                      )}
                                      <div className="p-2.5">
                                        <p className="text-xs font-semibold text-slate-800 line-clamp-2">
                                          {p.accroche || p.nom}
                                        </p>
                                        <p className="text-[10px] text-slate-400 mt-1 truncate">
                                          {p.nom}
                                        </p>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                {!visibles.length && (
                  <p className="text-sm text-slate-400 text-center py-16">Aucune campagne.</p>
                )}
              </div>
            ) : (
              <div className="flex gap-4 p-5 overflow-x-auto h-full">
                {ETATS.map((col) => {
                  const cartes = visibles.filter((c) => c.etat === col.cle);
                  const somme = cartes.reduce((t, c) => t + (c.depense || 0), 0);
                  return (
                    <div key={col.cle} className="w-[280px] shrink-0 flex flex-col min-h-0">
                      <div className="flex items-center gap-2 mb-2.5 px-1">
                        <span className={`w-2 h-2 rounded-full ${col.point}`} />
                        <span className="text-sm font-semibold text-slate-700">{col.titre}</span>
                        <span className="text-xs text-slate-400 tabular-nums">{cartes.length}</span>
                        <span className="ml-auto text-xs text-slate-500 tabular-nums">
                          {somme > 0 ? euros(somme) : ""}
                        </span>
                      </div>
                      <div className="space-y-2 overflow-y-auto pb-4">
                        {cartes.slice(0, 40).map((c) => (
                          <button key={c.id} onClick={() => setFiche(c)}
                            className="w-full text-left bg-white rounded-xl border border-slate-200
                                       p-3 hover:border-indigo-300 hover:shadow-sm transition">
                            <p className="text-sm font-medium text-slate-900 leading-snug line-clamp-2">
                              {c.nom}
                            </p>
                            <span className="inline-block mt-1.5 text-[10px] px-2 py-0.5 rounded-full
                                             bg-slate-100 text-slate-600 border border-slate-200">
                              {c.compte_nom}
                            </span>
                            <div className="flex items-center gap-2 mt-2">
                              <span className={`text-sm font-semibold tabular-nums ${
                                c.depense > 0 ? "text-slate-900" : "text-slate-300"}`}>
                                {c.depense > 0 ? euros(c.depense) : "0,00 €"}
                              </span>
                              <span className="ml-auto text-[11px] text-slate-400">
                                {c.clics ? `${entier(c.clics)} clics` : c.fin ? `fin ${c.fin}` : "—"}
                              </span>
                            </div>
                          </button>
                        ))}
                        {!cartes.length && (
                          <div className="border border-dashed border-slate-200 rounded-xl
                                          py-6 px-3 text-center text-xs text-slate-400">
                            {col.cle === "diffuse" ? "Rien ne tourne en ce moment." : "Aucune campagne"}
                          </div>
                        )}
                        {cartes.length > 40 && (
                          <p className="text-[11px] text-slate-400 text-center py-2">
                            {cartes.length - 40} de plus — affinez avec la recherche
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <aside className="w-[268px] shrink-0 border-l border-slate-200 bg-white p-4
                            overflow-y-auto hidden xl:block">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-2.5">
              Ce qui cloche
            </div>
            {!veille ? (
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> contrôle en cours…
              </div>
            ) : !(veille.alertes || []).length ? (
              <p className="text-xs text-slate-400">Rien à signaler.</p>
            ) : (
              <div className="space-y-2">
                {veille.alertes.map((a, i) => (
                  <div key={i} className={`rounded-lg p-2.5 text-[12px] ${
                    a.niveau === "grave" ? "bg-rose-50 text-rose-700"
                    : a.niveau === "tiede" ? "bg-amber-50 text-amber-800"
                    : "bg-slate-50 text-slate-600 border border-slate-200"}`}>
                    <strong className="block font-semibold">{a.titre}</strong>
                    <span className="opacity-90">{a.detail}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-4 pt-3 border-t border-slate-100 text-[11px] text-slate-400">
              Contrôlé en lisant les {comptes.length} comptes chez Meta.
            </div>
          </aside>
        </div>
      </div>

      {fiche && (
        <div className="fixed inset-0 z-40 flex justify-end" onClick={() => setFiche(null)}>
          <div className="absolute inset-0 bg-black/20" />
          <div className="relative w-[380px] max-w-[94vw] bg-white h-full shadow-2xl flex flex-col"
               onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-slate-100 flex items-start gap-3">
              <div className="min-w-0">
                <h3 className="font-semibold text-slate-900 leading-snug">{fiche.nom}</h3>
                <p className="text-[11px] text-slate-400 mt-0.5">
                  {fiche.compte_nom} · {fiche.objectif}
                </p>
              </div>
              <button onClick={() => setFiche(null)}
                      className="ml-auto text-slate-400 hover:text-slate-600 text-xl leading-none">×</button>
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              <div className="grid grid-cols-2 gap-2.5 mb-4">
                {[["dépensé", euros(fiche.depense)], ["clics", entier(fiche.clics)],
                  ["le clic", fiche.cpc ? euros(fiche.cpc) : "—"],
                  ["impressions", entier(fiche.impressions)]].map(([e, v]) => (
                  <div key={e} className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                    <div className="text-[15px] font-semibold text-slate-900 tabular-nums">{v}</div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">{e}</div>
                  </div>
                ))}
              </div>

              <div className="text-xs text-slate-500 mb-4 space-y-1">
                <p>Statut Meta : <span className="text-slate-800">{fiche.statut}</span></p>
                <p>Diffusion : <span className="text-slate-800">{fiche.diffusion}</span></p>
                {fiche.budget_jour && (
                  <p>Budget : <span className="text-slate-800">{fiche.budget_jour} €/jour</span></p>)}
                {fiche.fin && (
                  <p>Fin de diffusion : <span className="text-slate-800">{fiche.fin}</span></p>)}
              </div>

              <div className="grid gap-2">
                {fiche.statut === "ACTIVE" ? (
                  <Button onClick={() => changerStatut(fiche, "PAUSED")} disabled={occupe}
                          variant="outline" className="justify-start text-amber-700 border-amber-200">
                    <Pause className="w-4 h-4 mr-2" /> Mettre en pause chez Meta
                  </Button>
                ) : (
                  <Button onClick={() => changerStatut(fiche, "ACTIVE")} disabled={occupe}
                          className="justify-start">
                    <Play className="w-4 h-4 mr-2" /> Relancer la diffusion
                  </Button>
                )}
                <Button variant="outline" className="justify-start"
                        onClick={() => { setFiche(null); ouvrirFormulaire(fiche); }}>
                  <Sparkles className="w-4 h-4 mr-2" /> Nouvelles annonces avec l'IA
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {formulaire && (
        <div className="fixed inset-0 z-[55] bg-black/40 flex items-center justify-center p-6"
             onClick={() => !creation && setFormulaire(null)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl"
               onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-5 border-b border-slate-100">
              <h2 className="text-lg font-semibold text-slate-900">Créer une campagne</h2>
              <p className="text-sm text-slate-500 mt-0.5">
                Ce cadrage part à ChatGPT, qui écrit le plan et les annonces puis prépare les visuels.
              </p>
            </div>

            <div className="px-6 py-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-slate-600">Compte publicitaire</label>
                  <select value={formulaire.config_id}
                          onChange={(e) => setFormulaire({ ...formulaire, config_id: e.target.value })}
                          className="mt-1 w-full text-sm border border-slate-200 rounded-lg px-3 py-2">
                    {comptes.map((c) => (
                      <option key={c.id} value={c.id}>{c.nom} — {c.societe}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Objectif</label>
                  <select value={formulaire.objectif}
                          onChange={(e) => setFormulaire({ ...formulaire, objectif: e.target.value })}
                          className="mt-1 w-full text-sm border border-slate-200 rounded-lg px-3 py-2">
                    {OBJECTIFS.map((o) => <option key={o.id} value={o.id}>{o.libelle}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-slate-600">Zone ciblée</label>
                  <input value={formulaire.zone}
                         onChange={(e) => setFormulaire({ ...formulaire, zone: e.target.value })}
                         placeholder="Mayotte, La Réunion, Île Maurice…"
                         className="mt-1 w-full text-sm border border-slate-200 rounded-lg px-3 py-2" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Audience</label>
                  <input value={formulaire.audience}
                         onChange={(e) => setFormulaire({ ...formulaire, audience: e.target.value })}
                         placeholder="loueurs de matériel, entreprises de BTP…"
                         className="mt-1 w-full text-sm border border-slate-200 rounded-lg px-3 py-2" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-slate-600">Budget par jour (€)</label>
                  <input type="number" min="1" value={formulaire.budget_jour}
                         onChange={(e) => setFormulaire({ ...formulaire, budget_jour: e.target.value })}
                         className="mt-1 w-full text-sm border border-slate-200 rounded-lg px-3 py-2" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Durée (jours)</label>
                  <input type="number" min="1" value={formulaire.duree_jours}
                         onChange={(e) => setFormulaire({ ...formulaire, duree_jours: e.target.value })}
                         className="mt-1 w-full text-sm border border-slate-200 rounded-lg px-3 py-2" />
                  <p className="text-[11px] text-slate-400 mt-1">
                    Budget total prévisionnel :{" "}
                    {((Number(formulaire.budget_jour) || 0) *
                      (Number(formulaire.duree_jours) || 0)).toLocaleString("fr-FR")} €
                  </p>
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-slate-600">
                  Ce que vous voulez vendre, et sous quel angle
                </label>
                <Textarea rows={4} value={formulaire.consigne}
                          onChange={(e) => setFormulaire({ ...formulaire, consigne: e.target.value })}
                          placeholder="Vendre SmartRent360 aux loueurs de matériel. Insister sur les états des lieux papier et les litiges au retour."
                          className="mt-1 text-sm" />
              </div>

              <div>
                <label className="text-xs font-medium text-slate-600">Nom de la campagne</label>
                <input value={formulaire.titre}
                       onChange={(e) => setFormulaire({ ...formulaire, titre: e.target.value })}
                       placeholder="laissez vide, ChatGPT le proposera"
                       className="mt-1 w-full text-sm border border-slate-200 rounded-lg px-3 py-2" />
              </div>
            </div>

            <div className="px-6 py-4 border-t border-slate-100 flex items-center gap-3 flex-wrap">
              <label className="flex items-center gap-2 text-xs text-slate-500 cursor-pointer">
                <input type="checkbox" checked={autoVisuels}
                       onChange={(e) => setAutoVisuels(e.target.checked)}
                       className="rounded border-slate-300" />
                Dessiner les visuels
              </label>
              {modeles.length > 0 && (
                <select value={modele} onChange={(e) => setModele(e.target.value)}
                        className="text-xs border border-slate-200 rounded-lg px-2 py-1.5">
                  {modeles.map((m) => <option key={m.id} value={m.id}>{m.nom} · {m.cout}</option>)}
                </select>
              )}
              <div className="flex-1" />
              <Button variant="outline" onClick={() => setFormulaire(null)} disabled={creation}>
                Annuler
              </Button>
              <Button onClick={lancerCreation} disabled={creation}
                      className="bg-gradient-to-r from-violet-600 to-indigo-600">
                {creation ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                          : <Sparkles className="w-4 h-4 mr-1.5" />}
                Lancer la création
              </Button>
            </div>
          </div>
        </div>
      )}

      {studioOuvert && (
        <div className="fixed inset-0 z-50 flex justify-end" onClick={() => setStudioOuvert(false)}>
          <div className="absolute inset-0 bg-black/25" />
          <div className="relative w-[440px] max-w-[95vw] bg-white h-full shadow-2xl flex flex-col"
               onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-violet-600 shrink-0" />
              <select className="flex-1 min-w-0 text-sm border border-slate-200 rounded-lg px-2 py-1.5"
                      value={brief?.id || ""} onChange={(e) => ouvrirBrief(e.target.value)}>
                {!briefs.length && <option value="">Aucun brief</option>}
                {briefs.map((b) => (
                  <option key={b.id} value={b.id}>{b.titre} · {b.nb_visuels || 0} visuel(s)</option>
                ))}
              </select>
              <Button size="icon" variant="outline" onClick={() => ouvrirFormulaire(null)}>
                <Plus className="w-4 h-4" />
              </Button>
              <button onClick={() => setStudioOuvert(false)}
                      className="text-slate-400 hover:text-slate-600 text-xl leading-none px-1">×</button>
            </div>

            <div ref={filRef} className="flex-1 overflow-y-auto p-4 space-y-3">
              {!brief && (
                <p className="text-sm text-slate-400 text-center mt-8">
                  Créez un brief pour commencer.
                </p>
              )}
              {messages.map((m, i) => (
                <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                  <div className={`max-w-[88%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap ${
                    m.role === "user" ? "bg-indigo-600 text-white rounded-br-sm"
                                      : "bg-slate-100 text-slate-800 rounded-bl-sm"}`}>
                    {m.texte}
                  </div>
                </div>
              ))}
              {flux && (
                <div className="flex justify-start">
                  <div className="max-w-[88%] rounded-2xl rounded-bl-sm px-3.5 py-2.5 text-sm
                                  bg-slate-100 text-slate-800 whitespace-pre-wrap">{flux}</div>
                </div>
              )}
              {dessine && (
                <div className="flex items-center gap-2 text-xs text-violet-600">
                  <Wand2 className="w-3.5 h-3.5 animate-pulse" />
                  Visuel {dessine.rang}/{dessine.total} — « {dessine.titre} »…
                </div>
              )}
              {enCours && !flux && !dessine && (
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> ChatGPT réfléchit…
                </div>
              )}

              {!!visuels.length && (
                <div className="pt-2">
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-2">
                    Visuels · {visuels.filter((v) => v.etat === "valide").length} validé(s)
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {visuels.map((v) => (
                      <div key={v.id} className="rounded-lg border border-slate-200 overflow-hidden">
                        {v.fichier ? (
                          <button onClick={() => setAgrandi({ ...v,
                            url: `${API_URL}/api/pub/visuels/${v.id}/image${v.cache ? `?v=${v.cache}` : ""}` })}
                            className={`block w-full ${RATIOS[v.format] || "aspect-square"} bg-slate-100`}>
                            <img src={`${API_URL}/api/pub/visuels/${v.id}/image${v.cache ? `?v=${v.cache}` : ""}`}
                                 alt={v.titre} className="w-full h-full object-cover" />
                          </button>
                        ) : (
                          <div className={`${RATIOS[v.format] || "aspect-square"} bg-slate-50
                                          flex flex-col items-center justify-center text-slate-300`}>
                            {v.occupe ? <Loader2 className="w-5 h-5 animate-spin" />
                                      : <ImageIcon className="w-6 h-6" />}
                            <span className="text-[10px] mt-1">
                              {v.occupe ? "génération…" : "pas généré"}
                            </span>
                          </div>
                        )}
                        <div className="p-2">
                          <p className="text-[11px] font-medium text-slate-800 line-clamp-2">{v.titre}</p>
                          <div className="flex items-center gap-1 mt-1.5">
                            <button onClick={() => juger(v, "valide")}
                              className={`p-1 rounded ${v.etat === "valide"
                                ? "bg-emerald-100 text-emerald-700" : "text-slate-400 hover:bg-slate-100"}`}>
                              <Check className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => juger(v, "rejete")}
                              className={`p-1 rounded ${v.etat === "rejete"
                                ? "bg-rose-100 text-rose-700" : "text-slate-400 hover:bg-slate-100"}`}>
                              <X className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => regenerer(v)} disabled={v.occupe}
                                    className="p-1 rounded text-slate-400 hover:bg-slate-100 ml-auto">
                              <RefreshCw className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {brief && (
              <div className="p-3 border-t border-slate-100">
                <div className="flex gap-2 items-end">
                  <Textarea rows={2} className="resize-none text-sm" placeholder="Votre demande…"
                    value={saisie} onChange={(e) => setSaisie(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); envoyer(); }
                    }} />
                  <Button size="icon" onClick={() => envoyer()} disabled={enCours || !saisie.trim()}>
                    {enCours ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
                <div className="flex items-center gap-3 mt-2">
                  <label className="flex items-center gap-2 text-[11px] text-slate-500 cursor-pointer">
                    <input type="checkbox" checked={autoVisuels}
                           onChange={(e) => setAutoVisuels(e.target.checked)}
                           className="rounded border-slate-300" />
                    Dessiner les visuels
                  </label>
                  {modeles.length > 0 && (
                    <select value={modele} onChange={(e) => setModele(e.target.value)}
                            className="text-[11px] border border-slate-200 rounded px-1.5 py-1 ml-auto">
                      {modeles.map((m) => <option key={m.id} value={m.id}>{m.nom}</option>)}
                    </select>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {agrandi && (
        <div className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-8"
             onClick={() => setAgrandi(null)}>
          <div className="max-w-4xl w-full flex gap-5 items-start" onClick={(e) => e.stopPropagation()}>
            <img src={agrandi.url} alt={agrandi.titre || agrandi.nom}
                 className="flex-1 min-w-0 rounded-xl shadow-2xl" />
            <div className="w-72 shrink-0 bg-white rounded-xl p-5 space-y-3">
              <h3 className="font-semibold text-slate-900">{agrandi.titre || agrandi.nom}</h3>
              {agrandi.accroche && (
                <p className="text-sm font-medium text-slate-800">{agrandi.accroche}</p>)}
              <p className="text-sm text-slate-600 whitespace-pre-wrap">{agrandi.texte}</p>
              {agrandi.prompt && (
                <div className="pt-3 border-t border-slate-100">
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">
                    Consigne de génération
                  </p>
                  <p className="text-xs text-slate-500 leading-relaxed">{agrandi.prompt}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <PubTrackingAgent ouvert={agentSuiviOuvert} fermer={() => setAgentSuiviOuvert(false)}
                        token={token} campagnes={campagnes || []} comptes={comptes}
                        veille={veille} filtreCompte={filtreCompte} />
    </CRMLayout>
  );
}
